import React, { useState, useEffect } from 'react';
import { analyticsService, whatsappService } from '../services/api';
import { AutomationRule, MessageTemplate } from '../types';

const AutomationManager: React.FC = () => {
  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_event: 'status_change',
    trigger_status: '',
    trigger_delay_minutes: 0,
    target_template_id: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rulesRes, templatesRes] = await Promise.all([
        analyticsService.getAutomationRules(),
        whatsappService.getTemplates()
      ]);
      
      setAutomationRules(rulesRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      if (!newRule.name || !newRule.trigger_status || !newRule.target_template_id) {
        alert('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      await analyticsService.createAutomationRule(newRule);
      alert('Regra de automação criada com sucesso!');
      setShowCreateModal(false);
      setNewRule({
        name: '',
        description: '',
        trigger_event: 'status_change',
        trigger_status: '',
        trigger_delay_minutes: 0,
        target_template_id: 0
      });
      loadData();
    } catch (error) {
      console.error('Erro ao criar regra:', error);
      alert('Erro ao criar regra de automação');
    }
  };

  const formatDelay = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
    return `${Math.floor(minutes / 1440)} dia(s)`;
  };

  const statusOptions = [
    { value: 'novo', label: 'Novo' },
    { value: 'interessado', label: 'Interessado' },
    { value: 'contato_inicial', label: 'Contato Inicial' },
    { value: 'em_negociacao', label: 'Em Negociação' },
    { value: 'proposta_enviada', label: 'Proposta Enviada' },
    { value: 'reagendar', label: 'Reagendar' },
    { value: 'fechado_ganho', label: 'Fechado Ganho' },
    { value: 'fechado_perdido', label: 'Fechado Perdido' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">🤖 Automação WhatsApp</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          ➕ Nova Regra
        </button>
      </div>

      {/* Lista de Regras */}
      <div className="grid grid-cols-1 gap-6">
        {automationRules.map((rule) => (
          <div key={rule.id} className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-medium text-gray-900">{rule.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    rule.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {rule.is_active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-3">{rule.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Gatilho:</span>
                    <div className="text-gray-600">
                      Status → <span className="font-medium">{rule.trigger_status}</span>
                    </div>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700">Delay:</span>
                    <div className="text-gray-600">{formatDelay(rule.trigger_delay_minutes)}</div>
                  </div>
                  
                  <div>
                    <span className="font-medium text-gray-700">Template:</span>
                    <div className="text-gray-600">{rule.template_name || 'N/A'}</div>
                  </div>
                </div>

                {rule.template_content && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <span className="font-medium text-gray-700">Mensagem:</span>
                    <p className="text-sm text-gray-600 mt-1">{rule.template_content}</p>
                  </div>
                )}
                
                <div className="mt-3 text-xs text-gray-500">
                  Criado por {rule.created_by_name} em {new Date(rule.created_at).toLocaleDateString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {automationRules.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🤖</div>
          <h3 className="text-lg font-medium mb-2">Nenhuma regra de automação</h3>
          <p className="text-gray-400">Crie sua primeira regra para automatizar o envio de mensagens</p>
        </div>
      )}

      {/* Modal de Criar Regra */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Nova Regra de Automação</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Regra *
                </label>
                <input
                  type="text"
                  value={newRule.name}
                  onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Ex: Primeiro contato para leads novos"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="Descreva quando esta regra será executada..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status Gatilho *
                  </label>
                  <select
                    value={newRule.trigger_status}
                    onChange={(e) => setNewRule({ ...newRule, trigger_status: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Selecione um status</option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delay (minutos)
                  </label>
                  <input
                    type="number"
                    value={newRule.trigger_delay_minutes}
                    onChange={(e) => setNewRule({ ...newRule, trigger_delay_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="0"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    0 = imediato, 60 = 1 hora, 1440 = 1 dia
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template de Mensagem *
                </label>
                <select
                  value={newRule.target_template_id}
                  onChange={(e) => setNewRule({ ...newRule, target_template_id: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value={0}>Selecione um template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview do template selecionado */}
              {newRule.target_template_id > 0 && (
                <div className="p-3 bg-blue-50 rounded-md">
                  <div className="text-sm font-medium text-blue-800 mb-1">Preview da mensagem:</div>
                  <div className="text-sm text-blue-700">
                    {templates.find(t => t.id === newRule.target_template_id)?.content}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateRule}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Criar Regra
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationManager;